import * as React from "react";
import ReactDOMServer from "react-dom/server";
import "./results.css";
import { useLocation } from "react-router-dom";
import BackButtonReport from "../BackButtonReport";
import PrintButton from "../PrintPage";
import { marked } from "marked";
import { cssForPDF } from "../../utilities/exportCSS";
import { insertOpenIcon, replaceGreaterThan } from "../../functions/addOpenInNewTab";
import { useEffect } from "react";
import useFetchWithMsal from "../../hooks/useFetchWithMsal";
import { appRoles, protectedResources } from "../../authConfig";
import icon from "../icons/open.svg"
import { useMsal } from "@azure/msal-react";
import CircularProgress from '@mui/material/CircularProgress';
import ReportTemplate from './webReportTemplate';
import PDFReportTemplate from './pdfReportTemplate';
import HTMLReportTemplate from './htmlReportTemplate';

export default function Report() {
  const location = useLocation();
  const { state } = location;
  const { instance } = useMsal();
  const account = instance.getActiveAccount();
  const userID = account.localAccountId;

  const [showTable, setShowTable] = React.useState(false);
  const [newData, setNewData] = React.useState();
  const [headerData, setHeaderData] = React.useState(undefined);
  const [isMyOwnTest, setIsMyOwnTest] = React.useState(false);
  const [isDownloadingPDF, setIsDownloadingPDF] = React.useState(false);
  let company = "";
  let date = "";
  let score = 0;
  let urlTested = "";
  let filteredResults = [];
  let testid = 0;
  let showBackButton = false;

  const params = new URLSearchParams(location.search);

  const { execute } = useFetchWithMsal({
    scopes: protectedResources.getReport.scopes.read
  });

  useEffect(() => {
    let classesList = ['links-markdown', 'links-markdown:hover'];

    insertOpenIcon('guidance', classesList, false);
    replaceGreaterThan('guidance', false);
  }, [])

  if (state?.id && state?.showBackButton != null) {
    testid = state.id;
    showBackButton = state.showBackButton;
  } else if (params?.get("id") && params?.get("showBackButton")) {
    testid = params.get("id");
    showBackButton = params.get("showBackButton") === 'true';
  }

  useEffect(() => {
    if (!newData) {
      execute("GET", protectedResources.getReport.endpoint + testid, protectedResources.getReport.scopes).then((response) => {
        if (response) {
          let data = response;
          company = data[0].companyname;
          date = data[0].date
          score = data[0].testscore
          urlTested = data[0].projecturl;

          setHeaderData({
            company: data[0].companyname,
            date: data[0].date,
            score: data[0].testscore,
            urlTested: data[0].projecturl,
          });

          if (data[0].criteria) {
            setShowTable(true);
          }

          let resultFields = [];

          for (let i = 0; i < data.length; i++) {
            if (!data[i].shouldDisplay) {
              continue;
            }

            const newReportField = {
              id: i,
              criteria: data[i].criteria,
              guidance: data[i].feedback, // the previous version of the code was returning the guidance intro on the interface twice
              notes: data[i].notes,
              capability: data[i].capability,
              criteriaid: data[i].criteriaid,
              url: data[i].url,
              score: data[i].responsescore,
            };
            resultFields.push(newReportField);
          }

          //pre-processing of json data for graph
          filteredResults = resultFields.map((result) => ({
            criteria: result.criteria,
            guidance: result.guidance,
            urlNotes: result.notes,
            urlNotes: `${result.url} \n\n ${result.notes.replaceAll("\\n", "\n")}`,
            url: result.url,
            notes: result.notes.replaceAll("\\n", "\n"),
            capability: result.capability,
            criteriaid: result.criteriaid,
            url: result.url,
            score: result.score,
          }));
          setNewData(filteredResults);
        }
      });
    }
  }, [execute, newData]);

  useEffect(() => {
    if (testid) {
      execute("GET", protectedResources.getBasicInfo.endpoint + testid, protectedResources.getBasicInfo.scopes).then(
        (response) => {
          if (response?.length > 0 && response[0].userid) {
            setIsMyOwnTest(response[0].userid === userID || account.idTokenClaims['roles'].includes(appRoles.superAdmin));
          }
        }
      );
    }
  }, [execute, testid]);

  const replace_symbols = (htmlData) => {
    htmlData = htmlData.replaceAll(/&lt;/g, "<");
    htmlData = htmlData.replaceAll(/&gt;/g, ">");
    htmlData = htmlData.replaceAll(/&amp;/g, "&");
    htmlData = htmlData.replaceAll(/&quot;/g, "\"");
    htmlData = htmlData.replaceAll(/&apos;/g, "'");
    return htmlData;
  }

  // remove spaces or ";" or "," from link url
  const cleanLink = (link) => {
    let cleanedLink = link;
    // strip the spaces from the url if any are present
    cleanedLink = "http" + cleanedLink.replace(/\s+/g, '');

    const lastCharNum = cleanedLink.length - 1;
    
    // if link's last character is a ";" or ",", remove those characters
    if(cleanedLink.charAt(lastCharNum) === ";" || cleanedLink.charAt(lastCharNum) === ",") {
      cleanedLink = cleanedLink.slice(0, -1);
    } 

    return cleanedLink;
  }

  const buildGuidanceHtml = (guidanceMarkdown) => {
    // Pipeline for converting DB guidance markdown to valid XHTML for the PDF JAR.
    //
    // The DB field is a mix of:
    //   - Standard markdown (paragraphs, bold, links, backtick code spans)
    //   - Pre-existing <p class="code-inline">...</p> blocks (already correct HTML)
    //   - Bare HTML tags in prose (e.g. <track>, <main>) that should display
    //     as literal text "<track>" in the PDF, not as actual elements
    //
    // Stage 1: Stash pre-existing <p class="code-inline"> blocks so marked never touches them.
    // Stage 2: Run marked.parse() on the raw markdown — it handles backtick code spans
    //          (wrapping them in <code>...</code> with inner content entity-encoded) and
    //          markdown links, bold, etc. Raw HTML tags in prose are passed through as-is.
    // Stage 3: Entity-encode any remaining bare tags that are NOT safe structural HTML
    //          produced by marked (i.e. not <p>, <a>, <strong>, <em>, <ul>, <li>,
    //          <code>, <br>, <h1>-<h6>, <blockquote>, <hr>, <pre>).
    //          This turns stray <track>, <main> etc. into &lt;track&gt; display text.
    // Stage 4: Swap <code>...</code> -> <p class="code-inline"> for PDF CodeFont.
    // Stage 5: Restore stashed <p class="code-inline"> blocks.

    // Stage 1: stash pre-existing <p class="code-inline"> blocks.
    const stash = [];
    const stashed = guidanceMarkdown.replace(
      /<p class="code-inline">[\s\S]*?<\/p>/g,
      (match) => {
        stash.push(match);
        return `\x00CODESTASH${stash.length - 1}\x00`;
      }
    );

    // Stage 2: parse markdown -> HTML. marked entity-encodes backtick span content
    // correctly (e.g. `<div>` -> <code>&lt;div&gt;</code>). Raw HTML tags in prose
    // are passed through unchanged by marked's default behaviour.
    const parsed = marked.parse(stashed);

    // Stage 3: entity-encode any tag in the output that is NOT safe structural HTML
    // that marked itself produces. This turns stray prose tags like <track> or <main>
    // into &lt;track&gt; so they display as literal text in the PDF.
    //
    // Safe set: every element marked can generate from standard markdown + GFM.
    const SAFE_TAG_RE = /^\/?(?:p|a|strong|b|em|i|ul|ol|li|code|pre|br|h[1-6]|blockquote|hr|s|del|table|thead|tbody|tr|th|td)(?:\s|$|>|\/)/i;
    const sanitized = parsed.replace(/<(\/?[a-zA-Z][a-zA-Z0-9]*)([^>]*)?>/g, (match, tag, rest) => {
      if (SAFE_TAG_RE.test(tag + ((rest && rest.length) ? rest[0] : ' '))) return match;
      return match.replaceAll('<', '&lt;').replaceAll('>', '&gt;');
    });

    // Stage 4: swap <code>...</code> -> <p class="code-inline"> for PDF CodeFont.
    const swapped = sanitized.replace(/<code[^>]*>([\s\S]*?)<\/code>/gi, (_match, inner) => {
      return `<p class="code-inline">${inner}</p>`;
    });

    // Stage 5: restore stashed <p class="code-inline"> blocks.
    return swapped.replace(/\x00CODESTASH(\d+)\x00/g, (_match, idx) => stash[Number(idx)]);
  };

  /**
   * buildNotesHtml — for the PDF/Word export path.
   *
   * Pipeline:
   *   1. Entity-encode all HTML special characters so user-typed tags like
   *      <body> display as literal text rather than being parsed as markup.
   *   2. Wrap explicit backtick fences (``` ... ```) in <p class="code-block|code-inline"> (PDF CodeFont).
   *   3. Wrap explicit inline backtick spans (` ... `) in <p class="code-block|code-inline">.
   *   4. Auto-detect runs of lines that are PURELY HTML-like tags (i.e. the line
   *      contains nothing but encoded tags and whitespace). Contiguous such lines
   *      are joined into a single <p class="code-inline"> block.
   *      Lines that mix prose and tags are left as plain text — only the
   *      isolated tag-only parts are wrapped.
   *   5. Convert escaped \n sequences to <br/> for PDF line-breaks.
   */
  const buildNotesHtml = (notes) => {
    // Step 1: entity-encode.
    const escaped = notes
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');

    const stash = [];
    const stashPush = (html) => {
      const idx = stash.length;
      stash.push(html);
      return `\x00STASH${idx}\x00`;
    };

    // Steps 2 & 3: explicit backtick fences → stash immediately.
    let s = escaped
      .replace(/```([\s\S]*?)```/g, (_m, inner) =>
        stashPush(`<p class="code-block">${inner}</p>`))
      .replace(/`([^`\n]+)`/g, (_m, inner) =>
        stashPush(`<p class="code-inline">${inner}</p>`));

    // Step 4a: collect contiguous tag-starting lines into a block <p class="code-inline">
    // and stash immediately so step 5 can't corrupt internal newlines.
    const MARKUP_LINE_START_RE = /^\s*&lt;\/?[a-zA-Z]/;
    const lines = s.split('\n');
    const outputLines = [];
    let htmlBlock = [];

    const flushHtmlBlock = () => {
      if (htmlBlock.length > 0) {
        outputLines.push(stashPush(`<p class="code-inline">${htmlBlock.join('\n')}</p>`));
        htmlBlock = [];
      }
    };

    for (const line of lines) {
      if (MARKUP_LINE_START_RE.test(line)) {
        htmlBlock.push(line);
      } else {
        flushHtmlBlock();
        // Step 4b: inline pass — wrap paired open+content+close tag spans,
        // then any remaining lone tags, as inline code.
        const INLINE_ELEMENT_RE =
          /(&lt;([a-zA-Z][a-zA-Z0-9]*)(?:\s[^&>]*?)?&gt;)(.*?)(&lt;\/\2&gt;)|(&lt;\/?[a-zA-Z][a-zA-Z0-9]*(?:\s[^&>]*?)?&gt;)/gs;
        const inlined = line.replace(INLINE_ELEMENT_RE, (...args) => {
          const [, openTag, , content, closeTag, loneTag] = args;
          if (loneTag) return stashPush(`<p class="code-inline">${loneTag}</p>`);
          return stashPush(`<p class="code-inline">${openTag}${content}${closeTag}</p>`);
        });
        outputLines.push(inlined);
      }
    }
    flushHtmlBlock();

    // Step 5: convert escaped \n to <br/> and restore all stashed blocks.
    return outputLines
      .join('\n')
      .replaceAll('\\n', '<br/>')
      .replace(/\x00STASH(\d+)\x00/g, (_m, i) => stash[Number(i)]);
  };

  /**
   * buildNotesWebHtml — for the web report template (dangerouslySetInnerHTML).
   * buildNotesWebHtml — converts raw user notes to safe HTML for dangerouslySetInnerHTML.
   *
   * The only safe approach is to entity-encode EVERYTHING first, then wrap
   * recognised patterns in real HTML wrapper elements. This guarantees the
   * browser never interprets user-typed angle brackets as real HTML tags.
   *
   * Pipeline:
   *   1. Entity-encode &, <, >  (must run first and unconditionally).
   *   2. Stash triple-backtick fences as block <code> elements.
   *   3. Stash single-backtick spans as inline <code> elements.
   *   4a. Line-level: collect contiguous lines that START with &lt; into a
   *       block <code> element (stashed to protect internal newlines).
   *   4b. Inline: wrap paired &lt;tag&gt;CONTENT&lt;/tag&gt; spans — and
   *       lone tags — in inline <code> elements.
   *   5. Convert remaining newlines to <br />, then restore all stash items.
   */
  const buildNotesWebHtml = (notes) => {
    // ------------------------------------------------------------------
    // Step 1 — entity-encode ALL user input unconditionally.
    // replaceAll order matters: & must come first.
    // ------------------------------------------------------------------
    const escaped = notes
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;');

    // ------------------------------------------------------------------
    // Stash helpers: every <code> wrapper we produce gets a placeholder
    // so later passes can't accidentally alter its content.
    // ------------------------------------------------------------------
    const stash = [];
    const protect = (html) => { stash.push(html); return `\x00${stash.length - 1}\x00`; };
    const restore = (s) => s.replace(/\x00(\d+)\x00/g, (_, i) => stash[+i]);

    // ------------------------------------------------------------------
    // Steps 2 & 3 — explicit backtick fences (user wrote them on purpose).
    // ------------------------------------------------------------------
    let s = escaped
      .replace(/```([\s\S]*?)```/g, (_m, inner) =>
        protect(`<code class="notes-code-block">${inner}</code>`))
      .replace(/`([^`\n]+)`/g, (_m, inner) =>
        protect(`<code class="notes-code-inline">${inner}</code>`));

    // ------------------------------------------------------------------
    // Step 4a — block-level auto-detection.
    // Collect contiguous lines whose first non-whitespace chars are &lt;
    // (an entity-encoded opening or closing tag) into one block <code>.
    // Stash each block immediately so step 5 never touches its newlines.
    // ------------------------------------------------------------------
    const BLOCK_LINE_RE = /^\s*&lt;/;
    const lines = s.split('\n');
    const out = [];
    let blockLines = [];

    const flushBlock = () => {
      if (!blockLines.length) return;
      // Use <br /> inside the block so it works even without pre-wrap CSS.
      out.push(protect(`<code class="notes-code-block">${blockLines.join('<br />')}</code>`));
      blockLines = [];
    };

    for (const line of lines) {
      if (BLOCK_LINE_RE.test(line)) {
        blockLines.push(line);
      } else {
        flushBlock();
        // Step 4b — inline auto-detection on prose lines.
        // First pass: paired open+content+close  e.g. &lt;div&gt;foo&lt;/div&gt;
        // Second pass: lone / self-closing tags   e.g. &lt;br&gt; &lt;/footer&gt;
        const PAIRED_RE = /(&lt;([A-Za-z][A-Za-z0-9]*)(?:\s[^]*?)?&gt;)([^]*?)(&lt;\/\2&gt;)/g;
        const LONE_RE   = /(&lt;\/?[A-Za-z][A-Za-z0-9]*(?:\s[^]*?)?(?:\/)?&gt;)/g;

        const inlined = line
          .replace(PAIRED_RE, (_m, open, _tag, content, close) =>
            protect(`<code class="notes-code-inline">${open}${content}${close}</code>`))
          .replace(LONE_RE, (_m, tag) =>
            protect(`<code class="notes-code-inline">${tag}</code>`));

        out.push(inlined);
      }
    }
    flushBlock();

    // ------------------------------------------------------------------
    // Step 5 — convert remaining prose newlines to <br />.
    // Stash placeholders contain no newlines, so this is safe.
    // Restore all protected blocks last.
    // ------------------------------------------------------------------
    return restore(
      out.join('\n')
        .replaceAll('\\n', '<br />')
        .replaceAll('\n', '<br />')
    );
  };

  const generatePDFReport = async (element, filename, title, willDownloadPDF = true) => {
    setIsDownloadingPDF(true);  

    // Pre-process guidance: convert markdown → HTML before passing to the static template.
    // The PDF template itself does no markdown processing — it expects ready-to-render HTML.
    // Also strip any leading/trailing '$' from criteria — some DB records have this artifact
    // which in the old code was silently dropped by DOM rendering but now appears verbatim.
    const pdfData = newData.map(row => ({
      ...row,
      criteria: row.criteria.replace(/^\$+|\$+$/g, '').trim(),
      guidanceHtml: buildGuidanceHtml(row.guidance),
      notesHtml: buildNotesHtml(row.notes || ''),
    }));

    const pdfReportMarkup = ReactDOMServer.renderToStaticMarkup(
      <PDFReportTemplate
        title={title}
        headerData={headerData}
        params={params}
        showTable={showTable}
        testedURLsLinkList={testedURLsLinkList}
        newData={pdfData}
      />
    );

    // The working (old) approach used plain XHTML without an XML declaration.
    // Omit the prolog so the output matches what the a11yPDFGenerator expects.
    const pdfHtmlString = pdfReportMarkup;

    execute("POST", protectedResources.getPDF.endpoint, protectedResources.getPDF.scopes, { "html": pdfHtmlString, "filename": filename, "downloadPDF": willDownloadPDF }).then(
      (response) => {
        setIsDownloadingPDF(false);
        if (response) {
          try {
            downloadPDF(filename, response);
          } catch (e) {
            window.alert(`There was an issue creating the ${willDownloadPDF ? 'PDF' : 'HTML'} file. Please try again later or contact the AID development team if the error persists.`);
          }
        } else {
          window.alert(`There was an issue creating the ${willDownloadPDF ? 'PDF' : 'HTML'} file. Please try again later or contact the AID development team if the error persists.`);
        }
      }
    );
  }

  function downloadPDF(reportName, data) {
    const url = window.URL.createObjectURL(data);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', reportName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  }

  const generateDocument = (filename, title) => {
    // Build pre-processed row data — same guidance/notes pipeline as the PDF path.
    // The HTML template receives ready-to-inject HTML strings; it does no markdown parsing.
    const htmlData = newData.map(row => ({
      ...row,
      criteria: row.criteria.replace(/^\$+|\$+$/g, '').trim(),
      guidanceHtml: buildGuidanceHtml(row.guidance),
      notesHtml: buildNotesHtml(row.notes || ''),
    }));

    const markup = ReactDOMServer.renderToStaticMarkup(
      <HTMLReportTemplate
        headerData={headerData}
        params={params}
        showTable={showTable}
        testedURLsLinkList={testedURLsLinkList}
        newData={htmlData}
        generatedAt={new Date().toLocaleString()}
      />
    );

    // clone the document 
    const clonedDocument = document.cloneNode(true);

    // retrieve the cells with the edit button in them
    let editButtonList = clonedDocument.getElementsByClassName("editButton");

    if (editButtonList.length > 0) {

      // remove the cells with the edit button in them
      while (editButtonList.length > 0) {
        editButtonList[0].parentNode.removeChild(editButtonList[0]);
      }

      // remove the header with the title "actions"
      let actionsHeaderElement = clonedDocument.getElementsByClassName("edit");
      actionsHeaderElement[0].parentNode.removeChild(actionsHeaderElement[0]);
    }

    let modifyTestElement = clonedDocument.getElementsByClassName("bottom-margin");
    modifyTestElement.length > 0 && modifyTestElement[0].parentNode.removeChild(modifyTestElement[0]);

    let guidanceData = clonedDocument.getElementsByClassName("guidance");

    for (let i = 0; i < newData.length; i++) {
      // this if-else makes code appear properly in the word doc
      if (newData[i].guidance.includes("```")) {
        let guidance = newData[i].guidance.replaceAll("<", "&lt;").replaceAll(">", "&gt;");
        guidanceData[i].innerHTML = replace_symbols(marked.parse(guidance));
      } else {
        guidanceData[i].innerHTML = replace_symbols(marked.parse(guidanceData[i].innerHTML));
      }
    }

    // remove the open in new tab icon from the word doc 
    for (let i = 0; i < guidanceData.length; i++) {
      let listOfImgs = guidanceData[i].querySelectorAll("img");
      listOfImgs.forEach(element => {
        element.remove()
      });
    }

    // urlNotes cells are already correctly formatted by buildNotesWebHtml() —
    // entity-encoded tags like &lt;header&gt; display as literal text inside <code> blocks.
    // Do NOT run replace_symbols / marked.parse here: replace_symbols would decode
    // &lt; back to < turning displayed text into real DOM elements in the export.
    let urlNotesData = clonedDocument.getElementsByClassName("urlNotes");

    for (let i = 0; i < urlNotesData.length; i++) {
      let listOfImgs = urlNotesData[i].querySelectorAll("img");
      listOfImgs.forEach(element => {
        element.remove()
      });
    }

    const blob = new Blob(['\ufeff', markup], { type: 'application/msword' });
    const url = URL.createObjectURL(blob);

    // Create download link element
    let downloadLink = document.createElement("a");
    downloadLink.href = url;
    downloadLink.download = filename;
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
    URL.revokeObjectURL(url);
  }

  const createLinkList = () => {
    let linkList = [];

    if (!newData) return linkList;

    newData.forEach((data, index) => {
      if (data.url && data.url.includes("http")) {
        data.url.split("http").forEach(link => {
          if (link.length === 0) return;

          let linkString = cleanLink(link);
         
          // Find index if this link already exists in array
          const existingLinkIndex = linkList.findIndex(l => l.url === linkString);

          if (existingLinkIndex === -1) {
            // If the link does not exist in the link list, add it from scratch
            linkList.push({
              url: linkString,
              criteriaId: [ data.criteriaid ],
              title: `Page ${linkList.length + 1}`
            });
          } else {
            // if link exists, add this criteriaId to that object
            linkList[existingLinkIndex].criteriaId.push(data.criteriaid);
          }
        });
      }
    });

    return linkList;
  }

  const testedURLsLinkList = createLinkList();

  // Augment each row with pre-processed notesWebHtml so the web template can
  // render notes safely via dangerouslySetInnerHTML without user-typed HTML tags
  // (e.g. <body>, <header>) being interpreted as real DOM elements.
  const webReportData = newData
    ? newData.map(row => ({
        ...row,
        notesWebHtml: buildNotesWebHtml(row.notes || ''),
      }))
    : newData;

  return (
    <>
      {showBackButton && headerData && headerData.company &&
        <BackButtonReport companyName={headerData.company} />
      }
      <ReportTemplate
        headerData={headerData}
        params={params}
        isMyOwnTest={isMyOwnTest}
        testid={testid}
        showTable={showTable}
        testedURLsLinkList={testedURLsLinkList}
        newData={webReportData}
        icon={icon}
        showBackButton={showBackButton}
      />
      <div id="actionButtons">
        {headerData && headerData.company &&
          <PrintButton name="Print Page" documentName={`${headerData.company} Accessibility Test for ${headerData.company} Performed on ${headerData.date}`} />
        }
        {headerData && headerData.company &&
          <PrintButton name="Generate HTML Document" function={() => generateDocument(headerData.company + '-' + headerData.date + '.html', 'Accessibility Report')} documentName={`${headerData.company} Accessibility Test for ${headerData.company} Performed on ${headerData.date}`} />
        }
        {headerData && headerData.company &&
          <PrintButton name="Generate Accessible PDF" function={() => generatePDFReport("rcontainer", `Accessibility Report for ${headerData.company}, Performed on ${headerData.date}.pdf`, `Accessibility Report for ${headerData.company}, Performed on ${headerData.date}.pdf`)} documentName={`${headerData.company} Accessibility Test for ${headerData.company} Performed on ${headerData.date}`} />
        }
      </div>
      {headerData && headerData.company &&
        <div id="hiddenHTMLDownload">
          <PrintButton name="Download HTML version of PDF file" function={() => generatePDFReport("rcontainer", `Accessibility Report for ${headerData.company}, Performed on ${headerData.date}.html`, `Accessibility Report for ${headerData.company}, Performed on ${headerData.date}.html`, cssForPDF.data, false)} documentName={`${headerData.company} Accessibility Test for ${headerData.company} Performed on ${headerData.date}`} />
        </div>
      }
      { isDownloadingPDF &&
        <div id="pdfPopup">
          <p>Downloading PDF</p>
          <CircularProgress />
        </div>
      }
    </>
  )
}